import { createReducer } from '@reduxjs/toolkit';

import type { SvaraAchievementFamilyDto, SvaraFeaturedAchievementDto } from '../../services/api';
import { loginSuccess, logout } from '../auth/authActions';
import {
	achievementShowcaseErrorDismiss,
	achievementShowcaseRevert,
	achievementShowcaseSaveFailure,
	achievementShowcaseSaveStarted,
	achievementShowcaseSaveSuccess,
	achievementShowcaseToggle,
	achievementsHubFailure,
	achievementsHubRequest,
	achievementsHubSuccess,
	achievementsPlayerFeaturedFailure,
	achievementsPlayerFeaturedRequest,
	achievementsPlayerFeaturedSuccess,
} from './achievementsActions';

const NO_SHOWCASE_VERSION = -1;
const DEFAULT_MAX_FEATURED = 5;

export type AchievementsState = {
	families: SvaraAchievementFamilyDto[];
	unlockedCount: number;
	totalCount: number;
	maxFeatured: number;
	loaded: boolean;

	featuredFamilyKeys: string[];
	confirmedFeaturedFamilyKeys: string[];
	showcaseVersion: number;

	hubLoading: boolean;
	hubErrorKey?: string;
	showcaseSaving: boolean;
	saveErrorKey?: string;

	viewedFeatured: {
		playerId: string | null;
		featured: SvaraFeaturedAchievementDto[];
		loading: boolean;
	};
};

const initial: AchievementsState = {
	families: [],
	unlockedCount: 0,
	totalCount: 0,
	maxFeatured: DEFAULT_MAX_FEATURED,
	loaded: false,
	featuredFamilyKeys: [],
	confirmedFeaturedFamilyKeys: [],
	showcaseVersion: NO_SHOWCASE_VERSION,
	hubLoading: false,
	hubErrorKey: undefined,
	showcaseSaving: false,
	saveErrorKey: undefined,
	viewedFeatured: { playerId: null, featured: [], loading: false },
};

export const achievementsReducer = createReducer(initial, b => {
	b.addCase(achievementsHubRequest, s => {
		s.hubLoading = true;
		s.hubErrorKey = undefined;
	});
	b.addCase(achievementsHubSuccess, (s, a) => {
		s.hubLoading = false;
		s.loaded = true;
		s.families = a.payload.families ?? [];
		s.unlockedCount = a.payload.unlockedCount;
		s.totalCount = a.payload.totalCount;
		s.maxFeatured = a.payload.maxFeatured || DEFAULT_MAX_FEATURED;

		if (!s.showcaseSaving && a.payload.showcaseVersion >= s.showcaseVersion) {
			s.featuredFamilyKeys = a.payload.featuredFamilyKeys ?? [];
			s.confirmedFeaturedFamilyKeys = a.payload.featuredFamilyKeys ?? [];
			s.showcaseVersion = a.payload.showcaseVersion;
		}
	});
	b.addCase(achievementsHubFailure, (s, a) => {
		s.hubLoading = false;
		s.hubErrorKey = a.payload;
	});

	b.addCase(achievementShowcaseToggle, (s, a) => {
		const { familyKey } = a.payload;
		s.featuredFamilyKeys = s.featuredFamilyKeys.filter(key =>
			s.families.some(family => family.familyKey === key && family.currentTier !== null),
		);

		if (s.featuredFamilyKeys.includes(familyKey)) {
			s.featuredFamilyKeys = s.featuredFamilyKeys.filter(key => key !== familyKey);
			return;
		}

		const unlocked = s.families.some(
			family => family.familyKey === familyKey && family.currentTier !== null,
		);
		if (unlocked && s.featuredFamilyKeys.length < s.maxFeatured) {
			s.featuredFamilyKeys.push(familyKey);
		}
	});
	b.addCase(achievementShowcaseSaveStarted, s => {
		s.showcaseSaving = true;
		s.saveErrorKey = undefined;
	});
	b.addCase(achievementShowcaseSaveSuccess, (s, a) => {
		s.showcaseSaving = false;
		s.showcaseVersion = a.payload.version;
		s.confirmedFeaturedFamilyKeys = a.payload.featuredFamilyKeys;
	});
	b.addCase(achievementShowcaseSaveFailure, (s, a) => {
		s.showcaseSaving = false;
		s.featuredFamilyKeys = s.confirmedFeaturedFamilyKeys;
		s.saveErrorKey = a.payload.key;
	});
	b.addCase(achievementShowcaseErrorDismiss, s => {
		s.saveErrorKey = undefined;
	});
	b.addCase(achievementShowcaseRevert, s => {
		s.featuredFamilyKeys = s.confirmedFeaturedFamilyKeys;
	});

	b.addCase(achievementsPlayerFeaturedRequest, (s, a) => {
		s.viewedFeatured = { playerId: a.payload, featured: [], loading: true };
	});
	b.addCase(achievementsPlayerFeaturedSuccess, (s, a) => {
		if (s.viewedFeatured.playerId !== a.payload.playerId) return;

		s.viewedFeatured.featured = a.payload.featured;
		s.viewedFeatured.loading = false;
	});
	b.addCase(achievementsPlayerFeaturedFailure, (s, a) => {
		if (s.viewedFeatured.playerId !== a.payload) return;

		s.viewedFeatured.featured = [];
		s.viewedFeatured.loading = false;
	});

	b.addCase(logout, () => initial);
	b.addCase(loginSuccess, () => initial);
});

type WithAchievements = { achievements: AchievementsState };

export const selectAchievementFamilies = (s: WithAchievements) => s.achievements.families;
export const selectAchievementsLoaded = (s: WithAchievements) => s.achievements.loaded;
export const selectAchievementsHubLoading = (s: WithAchievements) => s.achievements.hubLoading;
export const selectAchievementsHubErrorKey = (s: WithAchievements) => s.achievements.hubErrorKey;
export const selectAchievementsUnlockedCount = (s: WithAchievements) =>
	s.achievements.unlockedCount;
export const selectAchievementsTotalCount = (s: WithAchievements) => s.achievements.totalCount;
export const selectAchievementsMaxFeatured = (s: WithAchievements) => s.achievements.maxFeatured;
export const selectFeaturedFamilyKeys = (s: WithAchievements) => s.achievements.featuredFamilyKeys;
export const selectShowcaseVersion = (s: WithAchievements) => s.achievements.showcaseVersion;
export const selectShowcaseSaveErrorKey = (s: WithAchievements) => s.achievements.saveErrorKey;
export const selectViewedFeatured = (s: WithAchievements) => s.achievements.viewedFeatured;
